import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";

const ACCEPT = "image/jpeg,image/png,image/webp";
const TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const MAX_BYTES = 8 * 1024 * 1024;

export interface LocalReference {
  id: string;
  file: File;
  url: string;
  serverId?: string; // set once uploaded to the project
}

// Free-form visual references (characters, places, wardrobe, style…). Kept in the browser until "Analyze".
export function ReferencePicker({
  items,
  onChange,
  onRemove,
  max,
  disabled,
}: {
  items: LocalReference[];
  onChange: (next: LocalReference[]) => void;
  onRemove?: (item: LocalReference) => void;
  max: number;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => () => items.forEach((i) => URL.revokeObjectURL(i.url)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = (files: FileList | null) => {
    if (!files) return;
    const next = [...items];
    for (const file of Array.from(files)) {
      if (!TYPES.has(file.type)) {
        toast.error(t("direct.refInvalid"));
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast.error(t("direct.refTooLarge"));
        continue;
      }
      if (next.length >= max) {
        toast.error(t("direct.refTooMany", { max }));
        break;
      }
      next.push({ id: `${Date.now()}-${next.length}-${file.name}`, file, url: URL.createObjectURL(file) });
    }
    onChange(next);
    if (input.current) input.current.value = "";
  };

  const remove = (id: string) => {
    const item = items.find((i) => i.id === id);
    if (item) {
      URL.revokeObjectURL(item.url);
      onRemove?.(item);
    }
    onChange(items.filter((i) => i.id !== id));
  };

  return (
    <div data-testid="reference-picker" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-[0.26em] text-[#7FD4FF]">
          {t("direct.references")} <span className="text-muted-foreground">· {t("common.optional")}</span>
        </span>
        <span className="font-display text-xs tracking-wider text-muted-foreground">{items.length}/{max}</span>
      </div>
      <p className="text-xs text-muted-foreground">{t("direct.referencesHint", { max })}</p>
      <div className="flex flex-wrap gap-3">
        {items.map((item, i) => (
          <div key={item.id} data-testid={`reference-thumb-${i}`} className="group relative h-24 w-24 overflow-hidden rounded-xl border border-[#2E7BFF]/35 bg-black/60">
            <img src={item.url} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              data-testid={`reference-remove-${i}`}
              aria-label={t("direct.removeReference")}
              disabled={disabled}
              onClick={() => remove(item.id)}
              className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/75 text-white opacity-90 transition-[opacity,background-color] hover:bg-[#F28B82] disabled:opacity-40"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {items.length < max && (
          <button
            type="button"
            data-testid="reference-add-btn"
            disabled={disabled}
            onClick={() => input.current?.click()}
            className="grid h-24 w-24 place-items-center rounded-xl border border-dashed border-[#2E7BFF]/45 bg-[#05070D]/60 text-[#7FD4FF] transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-[#7FD4FF] hover:shadow-[0_0_24px_-8px_rgba(46,123,255,0.9)] disabled:opacity-40"
          >
            <span className="flex flex-col items-center gap-1 text-[10px] font-semibold uppercase tracking-wider">
              <ImagePlus className="h-5 w-5" strokeWidth={1.75} />
              {t("common.add")}
            </span>
          </button>
        )}
      </div>
      <input ref={input} data-testid="reference-file-input" type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => add(e.target.files)} />
    </div>
  );
}
